import { useMutation } from '@apollo/client';
import { GENERATE_COMMUNITY_PROMPTS, GET_COMMUNITY_PROMPTS } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import type { PromptDetails, GenerateCommunityPromptsResponse as GenerateCommunityPromptsData } from '@core/graphql/types/schema';
import { track } from '@core/logging';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useState, useCallback } from 'react';

export interface GenerateCommunityPromptsInput {
  labelFeedback: Array<{
    labelName: string;
    falsePositives?: string[];
    falseNegatives?: string[];
  }>;
}

interface GenerateCommunityPromptsMutationResponse {
  generateCommunityPrompts: GenerateCommunityPromptsData;
}

export interface PromptGenerationResult {
  classification: Pick<PromptDetails, 'promptVersion' | 'isNew'>;
  moderation: Pick<PromptDetails, 'promptVersion' | 'isNew'>;
  username: Pick<PromptDetails, 'promptVersion' | 'isNew'>;
}

function toTypeResult(details?: PromptDetails): Pick<PromptDetails, 'promptVersion' | 'isNew'> {
  return {
    promptVersion: details?.promptVersion || '',
    isNew: details?.isNew ?? false,
  };
}

function toPromptResult(data?: GenerateCommunityPromptsData): PromptGenerationResult {
  return {
    classification: toTypeResult(data?.classification),
    moderation: toTypeResult(data?.moderation),
    username: toTypeResult(data?.username),
  };
}

function trackPromptSuccess(communityId: string, prompts: PromptGenerationResult) {
  track('GenerateCommunityPromptSucceeded', {
    communityId,
    classificationVersion: prompts.classification.promptVersion || 'unknown',
    moderationVersion: prompts.moderation.promptVersion || 'unknown',
    usernameVersion: prompts.username.promptVersion || 'unknown',
    classificationIsNew: String(prompts.classification.isNew),
    moderationIsNew: String(prompts.moderation.isNew),
    usernameIsNew: String(prompts.username.isNew),
  });
}

export function useGenerateCommunityPrompt() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [generatePromptsMutation, { loading }] = useMutation<GenerateCommunityPromptsMutationResponse>(GENERATE_COMMUNITY_PROMPTS, {
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const generatePrompts = useCallback(
    async (communityId: string, input: GenerateCommunityPromptsInput): Promise<{ success: boolean; result?: PromptGenerationResult }> => {
      try {
        setErrors(null);
        track('GenerateCommunityPromptInitiated', {
          communityId,
        });

        const response = await generatePromptsMutation({
          variables: {
            communityId,
            input: {
              labelFeedback: input.labelFeedback,
            },
          },
          refetchQueries: communityId ? [{ query: GET_COMMUNITY_PROMPTS, variables: { communityId } }] : [],
          awaitRefetchQueries: true,
        });

        const result = toPromptResult(response.data?.generateCommunityPrompts);

        trackPromptSuccess(communityId, result);

        return {
          success: true,
          result,
        };
      } catch (error) {
        track('GenerateCommunityPromptFailed', {
          communityId,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return {
          success: false,
        };
      }
    },
    [generatePromptsMutation]
  );

  return {
    generatePrompts,
    loading,
    errors,
    resetErrors,
  };
}
